import React, { useEffect, useState, useCallback } from 'react';
import { Button, Flex, Popconfirm, Select, DatePicker, Dropdown, message } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { EditOutlined, DeleteOutlined, MoreOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import dayjs from 'dayjs';
import FilterBar, { FilterField } from 'components/Common/FilterBar';
import useUrlFilters from 'hooks/useUrlFilters';

const { RangePicker } = DatePicker;

// Search, filters and the page live in the URL so the view can be shared as a link.
const URL_FILTERS = {
  search: { type: 'string' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 20 },
  status: { type: 'string' },
  dateRange: { type: 'dateRange', keys: ['start_date', 'end_date'] },
};

const STATUS_BADGE_BASE = {
  padding: "4px 12px",
  borderRadius: "9999px",
  fontSize: "12px",
  fontWeight: 500,
  whiteSpace: 'nowrap',
};

const getStatusStyle = (status) => {
  const colors = {
    DRAFT: "#6B7280",
    APPROVED: "#16A34A",
    CANCELLED: "#DC2626",
  };
  const color = colors[status] || "#6B7280";
  return { backgroundColor: `${color}20`, color, ...STATUS_BADGE_BASE };
};

const DebitNotesTable = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const currentPage = filters.page;
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const pagination = { current: currentPage, pageSize, total };
  const searchTerm = filters.search || '';
  // Typed-but-not-submitted text stays local; resync on back/forward.
  const [searchValue, setSearchValue] = useState(searchTerm);
  useEffect(() => setSearchValue(searchTerm), [searchTerm]);
  const statusFilter = filters.status || undefined;
  const setStatusFilter = (value) => setFilters({ status: value, page: 1 });
  const dateRange = filters.dateRange;
  const setDateRange = (value) => setFilters({ dateRange: value, page: 1 });

  // Search sits outside the filter panel, so it is neither counted nor cleared.
  const activeFilterCount =
    (statusFilter ? 1 : 0) + (dateRange && dateRange[0] && dateRange[1] ? 1 : 0);

  const clearFilters = () => {
    setFilters({ status: null, dateRange: null, page: 1 });
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, page_size: pageSize };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }
      const response = await axios.get('/creditnotes/debit-notes/', { params });
      if (response.status === 200) {
        setData(response.data.results || []);
        setTotal(response.data.count || 0);
      }
    } catch (err) {
      message.error('Failed to load debit notes');
    }
    setLoading(false);
  }, [searchTerm, statusFilter, dateRange, currentPage, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/creditnotes/debit-notes/${id}/`);
      message.success('Debit note deleted');
      loadData();
    } catch {
      message.error('Failed to delete');
    }
  };

  const handleTableChange = (pag) => {
    // A new page size starts again from page 1.
    setFilters({ page: pag.pageSize !== pageSize ? 1 : pag.current, pageSize: pag.pageSize });
  };

  const getDropdownItems = (record) => [
    {
      key: '1',
      label: (
        <a href={`/debit-notes/edit/${record.id}`} target="_blank" rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
          <EditOutlined style={{ marginRight: 8 }} /> Edit
        </a>
      ),
      onClick: e => e.domEvent.stopPropagation(),
    },
    {
      key: '2',
      label: (
        <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record.id)} onClick={e => e.stopPropagation()}>
          <a style={{ display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
            <DeleteOutlined style={{ marginRight: 8 }} /> Delete
          </a>
        </Popconfirm>
      ),
      onClick: e => e.domEvent.stopPropagation(),
    },
  ];

  const columns = [
    {
      title: 'DN Number',
      dataIndex: 'dn_number',
      key: 'dn_number',
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: 'Invoice',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
    },
    {
      title: 'Customer',
      dataIndex: ['account_data', 'name'],
      key: 'customer',
    },
    {
      title: 'Date',
      dataIndex: 'dn_date',
      key: 'dn_date',
      render: (date) => date ? dayjs(date).format('DD MMM YYYY') : '-',
    },
    {
      title: 'Total',
      dataIndex: 'total_rounded',
      key: 'total_rounded',
      render: (val) => val != null ? `₹${Number(val).toLocaleString('en-IN')}` : '-',
      align: 'right',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span style={getStatusStyle(status)}>{status}</span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_, record) => (
        <Dropdown menu={{ items: getDropdownItems(record) }} trigger={['click']} onClick={e => e.stopPropagation()}>
          <Button type="text" icon={<MoreOutlined />} onClick={e => e.stopPropagation()} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Debit Notes</h2>
      </Flex>
      <FilterBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={(value) => setFilters({ search: value, page: 1 })}
        searchPlaceholder="Search debit notes"
        activeCount={activeFilterCount}
        onClear={clearFilters}
        title="Filter Debit Notes"
        onRefresh={() => loadData()}
        refreshing={loading}
        addLabel="New Debit Note"
        onAdd={() => navigate('/debit-notes/new')}
      >
        <FilterField label="Status">
            <Select
              placeholder="Status"
              allowClear
              value={statusFilter}
              onChange={val => setStatusFilter(val)}
              options={[
                { value: 'DRAFT', label: 'Draft' },
                { value: 'APPROVED', label: 'Approved' },
                { value: 'CANCELLED', label: 'Cancelled' },
              ]}
            />
        </FilterField>
        <FilterField label="Date Range">
          <RangePicker value={dateRange} onChange={val => setDateRange(val)} />
        </FilterField>
      </FilterBar>
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} debit notes`,
        }}
        onChange={handleTableChange}
        onRow={(record) => ({
          onClick: () => navigate(`/debit-notes/edit/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
};

export default DebitNotesTable;
